import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesSeededitPortraitParams {
  /**
   * 输入图像URL，必填参数。支持JPG、JPEG、PNG格式
   */
  image_input: string;
  /**
   * 提示词，可选参数。默认为空字符串
   * @default ""
   */
  prompt?: string;
  /**
   * 输出图像宽度，可选参数。范围512-2048，默认1328
   * @default 1328
   * @range 512 - 2048
   */
  width?: number;
  /**
   * 输出图像高度，可选参数。范围512-2048，默认1328
   * @default 1328
   * @range 512 - 2048
   */
  height?: number;
  /**
   * 人脸增强参数，可选参数。范围0-1，默认0.4
   * @default 0.4
   * @values 0 | 0.1 | 0.2 | 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 | 1
   */
  gpen?: 0 | 0.1 | 0.2 | 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 | 1;
  /**
   * 皮肤美化参数，可选参数。范围0-1，默认0.3
   * @default 0.3
   * @values 0 | 0.1 | 0.2 | 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 | 1
   */
  skin?: 0 | 0.1 | 0.2 | 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 | 1;
  /**
   * 肤色统一参数，可选参数。范围0-1，默认0
   * @default 0
   * @values 0 | 0.1 | 0.2 | 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 | 1
   */
  skin_unifi?: 0 | 0.1 | 0.2 | 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 | 1;
  /**
   * 生成模式，可选参数。creative(创意模式)、reference(参考模式)、reference_char(参考角色模式)
   * @values creative | reference | reference_char
   */
  gen_mode?: 'creative' | 'reference' | 'reference_char';
  /**
   * 随机种子，可选参数。字符串类型，默认'-1'（随机）
   * @default "-1"
   */
  seed?: string;
}

export interface VolcesSeededitPortraitResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seededit_portrait
 *
 * Volces SeedEdit Portrait Photo Generator
 * 
 * Portrait photo generation implementation for Volces SeedEdit Portrait Photo (火山引擎SeedEdit人像摄影).
 * Model: SeedEdit Portrait Photo
 * Features: Portrait photo generation with face enhancement, skin beautification, and style control
 * Output: JPG, JPEG, PNG formats
 * Resolution: 512-2048px (default 1328x1328)
 * 
 * API Documentation: https://www.volcengine.com/docs/85128/1602212
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeededitPortrait } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeededitPortrait({
 *   // 参数见 VolcesSeededitPortraitParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeededitPortraitParams 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeededitPortrait(
  params: VolcesSeededitPortraitParams,
): Promise<VolcesSeededitPortraitResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seededit_portrait',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeededitPortraitResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
